import axios from 'axios';
import { message } from 'src/antd';
import React from 'react';
import { T } from '@transifex/react';
import { APIError } from '../consts/error';

type TVerificationType = 'sms' | 'email';

type TVariables = {
  token: string;
  captcha_token: string;
  verification_type: TVerificationType;
  phone_number?: string;
  email?: string;
};

type TResponse = {
  /**
   * ISO-8601 string, NOT epoch milliseconds — the backend serialises this with
   * `datetime.isoformat()`. It was typed `number` here, so the countdown that
   * consumes it computed NaN and collapsed to 0, which is why the re-verify
   * wait never appeared and re-entering a phone number looked impossible
   * (PRO-635).
   */
  next_attempt_timestamp: string;
  /** Localized, already names the wait in minutes. Worth showing. */
  message?: string;
};

export const mutateSendVerificationCode = ({
  token,
  verification_type,
  email,
  phone_number,
  captcha_token,
}: TVariables): Promise<{ data: TResponse }> =>
  axios
    .post(
      `notifications/send-verification-code`,
      {
        verification_type,
        email,
        phone_number,
        captcha_token,
      },
      {
        headers: {
          'X-API-Key': token,
        },
      }
    )
    .catch((e) => {
      // Carry the body through on BOTH branches. A 400 here is usually the
      // cooldown, and it ships `next_attempt_timestamp` — the caller turns that
      // into the countdown the citizen actually needs. Attaching it only when a
      // `message` happens to be present would make the countdown depend on
      // whether the error was worded, which is not a property it should have.
      const data = e?.response?.data;

      if (data?.message) {
        message.error(data.message);
        throw new APIError(data.message, {
          ERROR_MESSAGE_HANDLED: true,
          data,
        });
      } else {
        message.error(
          <T _str="Something went wrong. Please try again later." />
        );
        throw new APIError('invalid_phone_number', {
          ERROR_MESSAGE_HANDLED: true,
          data,
        });
      }
    });
